import { endOfMonth, format, startOfMonth } from "date-fns";
import notify from "../../../lib/notify";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  downloadCalendarData2,
  getAbsences2,
  getAbsenceTypes2,
  getAbsenceTypesForUser2,
  getAnnualLeaveLimits2,
  getHolidayLimit2,
  getSubordinates2,
  fetchWorkPlan,
  fetchWorkPlanForManager,
} from "../../../api/absence2";
import type { AnnualLeaveEntry } from "../../../api/calendarData";
import {
  buildEmploymentIndex,
  EmploymentIndex,
  getEmploymentPeriods,
} from "../../../lib/employment";
import { useAuth } from "../../../contexts/auth";
import { useCompanyContext } from "../../../contexts/company";
import { useLoader } from "../../../hooks/useLoading";
import { RoleEnum } from "../../../types";
import { DEFAULT_PAGE_SIZE } from "../constants";
import type {
  AbsenceNameOption2,
  HolidayDataMap2,
  ScheduleDay2,
  UserForSelect2,
  ViewMode2,
} from "../types";

export function useHoliday2Data() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();
  const loader = useLoader();

  const role = user?.roles;
  const isHeadAdministrator = role?.includes(RoleEnum.AdministratorGlowny);
  const isHR =
    role?.includes("KadrySpolki") ||
    role?.includes("AdministratorSpolki") ||
    role?.includes("AdministratorGlowny");
  const isNotEmployee = role && !role.includes(RoleEnum.Pracownik);
  const isNotForeman = role && !role.includes(RoleEnum.Majster);
  const isForeman = role?.includes(RoleEnum.Majster);
  const isTimeManager = user?.isTimeManager;

  const subordinatesCompanyId =
    isHeadAdministrator && companyId !== 0 ? companyId : undefined;

  const [viewMode, setViewMode] = useState<ViewMode2>("Employee");
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [schedule, setSchedule] = useState<Record<string, ScheduleDay2>>({});
  const [holidayData, setHolidayData] = useState<HolidayDataMap2>({});
  const [entries, setEntries] = useState<AnnualLeaveEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [gridSearchQuery, setGridSearchQuery] = useState("");
  const [users, setUsers] = useState<UserForSelect2[]>([]);
  const [names, setNames] = useState<AbsenceNameOption2[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [holidayLimitUser, setHolidayLimitUser] = useState({ days: 0, hours: 0 });
  // Przedziały zatrudnienia widocznych pracowników - do blokady dni poza umową na urlopach.
  const [employmentIndex, setEmploymentIndex] = useState<EmploymentIndex>(
    new Map(),
  );

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(gridSearchQuery), 300);
    return () => clearTimeout(handler);
  }, [gridSearchQuery]);

  // Fetch subordinate employees
  const fetchSubordinates = useCallback(async () => {
    try {
      const from = format(startOfMonth(currentMonth), "yyyy-MM-dd");
      const to = format(endOfMonth(currentMonth), "yyyy-MM-dd");
      const response = await getSubordinates2(subordinatesCompanyId, from, to);
      if (response.isOk && response.data) {
        setUsers(response.data as unknown as UserForSelect2[]);
      }
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  }, [t, subordinatesCompanyId, currentMonth]);

  // Fetch absence types
  useEffect(() => {
    const fetchAbsenceTypes = async () => {
      if (!isNotEmployee) {
        const response = await getAbsenceTypesForUser2();
        if (response.isOk && response.data) {
          setNames(response.data.map((d) => ({ name: d.name, rozliczDni: d.rozliczDni })));
        }
      }
      if (isNotEmployee || isTimeManager) {
        const response = await getAbsenceTypes2();
        if (response.isOk && response.data) {
          setNames(
            response.data.map((d) => ({
              name: d.name,
              rozliczDni: d.rozliczDni,
              companyIds: d.companyIds,
            })),
          );
        }
      }
    };
    fetchAbsenceTypes();
  }, [isNotEmployee, isTimeManager]);

  // Fetch subordinates on mount
  useEffect(() => {
    if (isNotEmployee || isTimeManager) {
      fetchSubordinates();
    }
  }, [isNotEmployee, isTimeManager, fetchSubordinates]);

  // Fetch absences for specific employees
  const fetchAbsences = useCallback(
    async (year: number, month: number, employeeIds: number[], companyIds: number[]) => {
      if (employeeIds.length === 0 || companyIds.length === 0) return;

      const ceids = companyIds.map((cid, i) => ({
        companyId: cid,
        employeeId: employeeIds[i] ?? 0,
      }));

      const response = await getAbsences2(year, month, ceids);
      if (response.isOk && response.data) {
        const newHolidayData: HolidayDataMap2 = { ...holidayData };
        for (const id of employeeIds) {
          const employeeData = response.data.find((d) => d.employeeId === id);
          newHolidayData[id] = (employeeData?.absences || []).map((a) => ({
            ...a,
            confirmed: Number(a.confirmed ?? 0),
            synch: Number(a.synch ? 1 : 0),
            transferred: Number(a.transferred ?? 0),
          }));
        }
        setHolidayData(newHolidayData);
      }
    },
    [holidayData],
  );

  // Fetch employee calendar data
  const fetchCalendarData = useCallback(
    async (year: number, month: number) => {
      setIsLoading(true);
      try {
        const result = await downloadCalendarData2(year, month);
        if (result.isOk && result.data) {
          const formattedData: Record<string, ScheduleDay2> = {};
          for (const day of result.data) {
            const hours =
              day.hours
                .map((h) => `${h.fromHour}-${h.toHour} (${h.duration})`)
                .join("\n") || "-";
            formattedData[day.date.split("T")[0]] = { hours, dayType: day.dayType };
          }
          setSchedule(formattedData);
        }
      } catch {
        notify(t("common.unknownErrorOccurred"), "error");
      } finally {
        setIsLoading(false);
      }
    },
    [t],
  );

  // Fetch holiday limit
  const fetchHolidayLimit = useCallback(
    async (year: number, month: number) => {
      try {
        const data = await getHolidayLimit2(year, month);
        setHolidayLimitUser({
          days: data.remainingAnnualLeaveDays,
          hours: data.remainingAnnualLeaveHours,
        });
      } catch (error) {
        notify(error instanceof Error ? error.message : t("common.unknownErrorOccurred"), "error");
      }
    },
    [t],
  );

  // Fetch AllEmployees view data
  const fetchAllEmployeesData = useCallback(
    async (date: Date) => {
      if (viewMode !== "AllEmployees") return;
      if (!isNotEmployee && !isTimeManager) return;

      setIsLoading(true);

      const formattedStartDate = format(startOfMonth(date), "yyyy-MM-dd");
      const formattedEndDate = format(endOfMonth(date), "yyyy-MM-dd");

      let result;
      if (user?.roles?.includes("Manager")) {
        result = await fetchWorkPlanForManager(formattedStartDate, formattedEndDate, pageIndex, pageSize);
      } else {
        const company = companyId === 0 ? user?.companyId || 0 : companyId;
        result = await fetchWorkPlan(
          formattedStartDate,
          formattedEndDate,
          company,
          user?.employeeId || 0,
          pageIndex,
          pageSize,
          false,
          debouncedSearch || undefined,
        );
      }

      if (result.isOk && result.data) {
        setEntries(result.data.items);
        setTotalCount(result.data.totalCount);

        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const empIds = result.data.items.map((e) => e.employee.id);
        const cmpIds = result.data.items.map((e) => e.employee.companyId);

        await fetchAbsences(year, month, empIds, cmpIds);

        // przedziały zatrudnienia widocznych pracowników - do blokady dni poza umową
        const spanEmployees = result.data.items.map((e) => ({
          companyId: e.employee.companyId,
          employeeId: e.employee.id,
        }));
        getEmploymentPeriods(spanEmployees, formattedStartDate, formattedEndDate)
          .then((p) => setEmploymentIndex(buildEmploymentIndex(p)))
          .catch(() => setEmploymentIndex(new Map())); // fail-open

        // Fetch annual leave limits
        const employeeCEIDs = result.data.items.map((e) => ({
          companyId: e.employee.companyId,
          employeeId: e.employee.id,
        }));

        getAnnualLeaveLimits2(year, month, employeeCEIDs)
          .then((limits) => {
            setEntries((prev) =>
              prev.map((entry) => {
                const limit = limits.find(
                  (l) => l.companyId === entry.employee.companyId && l.employeeId === entry.employee.id,
                );
                return {
                  ...entry,
                  remainingAnnualLeave: limit
                    ? { days: limit.numberOfDays, hours: limit.numberOfHours }
                    : null,
                };
              }),
            );
          })
          .catch(() => {});
      } else {
        setEntries([]);
        setTotalCount(0);
      }

      setIsLoading(false);
    },
    [viewMode, isNotEmployee, isTimeManager, user, companyId, pageIndex, pageSize, debouncedSearch, fetchAbsences],
  );

  // Employee view: fetch data when month changes
  useEffect(() => {
    let isCancelled = false;

    const fetchAll = async () => {
      if (viewMode === "AllEmployees") return;

      setIsLoading(true);
      loader?.showLoader();
      try {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;

        const visibleEmployeeIds = [user?.employeeId ?? 0];
        const visibleCompanyIds = [user?.companyId ?? 0];

        await Promise.all([
          fetchCalendarData(year, month),
          fetchAbsences(year, month, visibleEmployeeIds, visibleCompanyIds),
          fetchHolidayLimit(year, month),
        ]);

        // własny okres zatrudnienia - do blokady dni poza umową we własnym kalendarzu urlopów
        const from = format(startOfMonth(currentMonth), "yyyy-MM-dd");
        const to = format(endOfMonth(currentMonth), "yyyy-MM-dd");
        getEmploymentPeriods(
          [
            {
              companyId: user?.companyId ?? 0,
              employeeId: user?.employeeId ?? 0,
            },
          ],
          from,
          to,
        )
          .then((p) => {
            if (!isCancelled) setEmploymentIndex(buildEmploymentIndex(p));
          })
          .catch(() => {
            if (!isCancelled) setEmploymentIndex(new Map()); // fail-open
          });
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
          loader?.hideLoader();
        }
      }
    };

    fetchAll();
    return () => {
      isCancelled = true;
    };
  }, [currentMonth, viewMode, companyId]);

  // AllEmployees view: fetch data
  useEffect(() => {
    fetchAllEmployeesData(currentMonth);
  }, [currentMonth, viewMode, pageIndex, pageSize, debouncedSearch, companyId]);

  return {
    viewMode,
    setViewMode,
    currentMonth,
    setCurrentMonth,
    schedule,
    holidayData,
    setHolidayData,
    entries,
    setEntries,
    totalCount,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    gridSearchQuery,
    setGridSearchQuery,
    users,
    setUsers,
    names,
    isLoading,
    holidayLimitUser,
    employmentIndex,
    isHR: !!isHR,
    isNotEmployee: !!isNotEmployee,
    isNotForeman: !!isNotForeman,
    isForeman: !!isForeman,
    isTimeManager: !!isTimeManager,
    fetchAbsences,
    fetchAllEmployeesData,
  };
}
